// @ts-nocheck
// app/quiz/results.tsx
// ✅ Standalone results page — navigated to from quiz/index.tsx after lead capture
// Receives quiz results via router params and renders full analysis + dual CTA.

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  trackPageView,
  trackButtonClick,
  trackFunnelStep,
} from '@/utils/analytics';
import { usePageEngagement } from '@/hooks/usePageEngagement';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  TouchableOpacity,
  Animated,
  Share,
} from 'react-native';
import Head from 'expo-router/head';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import {
  getAccessibleBackgroundColor,
  getAccessibleColor,
  getAccessibleTextColor,
  getAccessibleFontFamily,
  shouldReduceMotion,
} from '@/utils/accessibilityHelpers';
import type { ParsedQuizContext } from '@/types/chat';

// ─── Types ────────────────────────────────────────────────────────────────────

type TrackKey =
  | 'programming' | 'data_ai' | 'digital_marketing' | 'ui_ux'
  | 'stock_market' | 'psychometric' | 'business_management'
  | 'content_creation' | 'customer_service'
  | 'cyber' | 'cloud_devops';

type Track = {
  key: TrackKey;
  title: string;
  subtitle: string;
  bullets: string[];
  href: string;
  emoji: string;
  color: string;
};

// ─── Track definitions (same as quiz/index.tsx) ───────────────────────────────

const TRACKS: Record<TrackKey, Track> = {
  programming: {
    key: 'programming', title: 'תכנות ופיתוח',
    subtitle: 'בניית מערכות, פתרון בעיות לוגיים ויצירת כלים שאנשים משתמשים בהם מדי יום.',
    bullets: ['פרויקטים מעשיים ותיק עבודות', 'עבודה עצמאית ומרחוק', 'שכר גבוה כבר בשנה הראשונה'],
    href: '/courses/programming', emoji: '💻', color: '#3b82f6',
  },
  data_ai: {
    key: 'data_ai', title: 'דאטה ובינה מלאכותית',
    subtitle: 'ניתוח נתונים, מודלים ותובנות — המקצוע הכי מבוקש בעשור הנוכחי.',
    bullets: ['SQL, Python, Machine Learning', 'תובנות עסקיות שמשנות החלטות', 'ביקוש עצום בשוק'],
    href: '/courses/data-ai', emoji: '🧠', color: '#8b5cf6',
  },
  digital_marketing: {
    key: 'digital_marketing', title: 'שיווק דיגיטלי',
    subtitle: 'קמפיינים, קריאייטיב ומדידת תוצאות — שילוב מושלם של יצירתיות ונתונים.',
    bullets: ['PPC, SEO, Social Media', 'תוצאות שמדידות ביום הראשון', 'גמישות לעבוד מכל מקום'],
    href: '/courses/digital-marketing', emoji: '📣', color: '#ec4899',
  },
  ui_ux: {
    key: 'ui_ux', title: 'עיצוב חוויית משתמש',
    subtitle: 'עיצוב מוצרים דיגיטליים — שם שיצירה פוגשת חשיבה מוצרית.',
    bullets: ['Figma ו-Design Systems', 'מחקר משתמשים אמיתי', 'תיק עבודות ויזואלי מרשים'],
    href: '/courses/ui-ux', emoji: '🎨', color: '#f59e0b',
  },
  stock_market: {
    key: 'stock_market', title: 'שוק ההון',
    subtitle: 'מסחר, ניתוח ופיננסים — למי שמרגיש נוח עם סיכון מחושב ואוהב שווקים.',
    bullets: ['ניהול תיקי השקעות', 'ניתוח טכני ופונדמנטלי', 'אסטרטגיות מסחר מתקדמות'],
    href: '/courses/stock-market', emoji: '📈', color: '#10b981',
  },
  psychometric: {
    key: 'psychometric', title: 'פסיכומטרי ומכינות',
    subtitle: 'הכנה למבחנים אקדמיים — מסגרת ברורה, יעדים מדידים, תוצאות מוכחות.',
    bullets: ['תוכנית מובנית ומותאמת', 'סימולציות בתנאי מבחן אמיתיים', 'מעקב התקדמות שבועי'],
    href: '/courses/psychometric', emoji: '📝', color: '#06b6d4',
  },
  business_management: {
    key: 'business_management', title: 'ניהול עסקי',
    subtitle: 'הובלת צוותים ופרויקטים — מתאים לאנשים שמניעים אחרים ואוהבים לקבל החלטות.',
    bullets: ['ניהול פרויקטים ומתודולוגיות', 'הובלת צוותים בסביבות משתנות', 'חשיבה אסטרטגית ועסקית'],
    href: '/courses/business-management', emoji: '💼', color: '#6366f1',
  },
  content_creation: {
    key: 'content_creation', title: 'יצירת תוכן',
    subtitle: 'כתיבה, וידאו וסושיאל — לאנשים יצירתיים שרוצים לספר סיפורים שמזיזים.',
    bullets: ['כתיבה שיווקית ויצירתית', 'עריכת וידאו ורילסים', 'בניית קהילה ומותג אישי'],
    href: '/courses/content-creation', emoji: '🎬', color: '#ef4444',
  },
  customer_service: {
    key: 'customer_service', title: 'שירות ותמיכה',
    subtitle: 'עבודה עם לקוחות ופתרון בעיות — למי שאנרגיה שלו מגיעה מעזרה לאחרים.',
    bullets: ['תקשורת בין-אישית מתקדמת', 'פתרון בעיות תחת לחץ', 'עבודה עם מערכות CRM'],
    href: '/courses/customer-service', emoji: '🤝', color: '#14b8a6',
  },
  cyber: {
    key: 'cyber', title: 'סייבר ואבטחת מידע',
    subtitle: 'הגנה על מערכות, גילוי חולשות ואבטחת נתונים — אחד המקצועות הכי מבוקשים בישראל.',
    bullets: ['Ethical Hacking ובדיקות חדירה', 'אבטחת רשתות ומערכות', 'ביקוש עצום בהייטק ובממשל'],
    href: '/courses/cyber', emoji: '🔒', color: '#ef4444',
  },
  cloud_devops: {
    key: 'cloud_devops', title: 'ענן ו-DevOps',
    subtitle: 'תשתיות ענן, אוטומציה ו-CI/CD — הבסיס שעליו רץ כל מוצר דיגיטלי מודרני.',
    bullets: ['AWS, Azure, GCP', 'Docker, Kubernetes, CI/CD', 'שכר מהגבוהים בתעשייה'],
    href: '/courses/cloud-devops', emoji: '☁️', color: '#0ea5e9',
  },
};

const VALID_TRACK_KEYS = Object.keys(TRACKS) as TrackKey[];

function isValidTrackKey(k: string): k is TrackKey {
  return VALID_TRACK_KEYS.includes(k as TrackKey);
}

// ─── Share helper ─────────────────────────────────────────────────────────────

async function shareResult(trackTitle: string): Promise<boolean> {
  const message = `גיליתי שאני מתאים ל${trackTitle}! גלה גם את המקצוע שמתאים לך 🎯`;
  const url = 'https://kursiaii.com/quiz';
  if (Platform.OS !== 'web') {
    try { await Share.share({ message: `${message} ${url}` }); return false; } catch {}
  }
  if (
    Platform.OS === 'web' &&
    typeof navigator !== 'undefined' &&
    navigator.share &&
    typeof navigator.canShare === 'function'
  ) {
    try { await navigator.share({ title: 'שאלון המקצוע של Kursi', text: message, url }); return false; } catch {}
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    try { await navigator.clipboard.writeText(`${message} ${url}`); return true; } catch {}
  }
  return false;
}

// ─── Component ────────────────────────────────────────────────────────────────

const isWeb = Platform.OS === 'web';

export default function QuizResultsPage() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    quizTrack?: string;
    quizTrackTitle?: string;
    quizTop?: string;
    quizLevels?: string;
    quizLeadCaptured?: string;
    quizPoints?: string;
  }>();
  const { settings } = useAccessibility();

  // ─── Parse params ─────────────────────────────────────────────────────────

  const ctx = useMemo<ParsedQuizContext>(() => {
    const rawTrack = (params.quizTrack ?? '').toString().trim();
    const trackKey = isValidTrackKey(rawTrack) ? rawTrack : 'digital_marketing';
    const trackTitle = params.quizTrackTitle?.toString().trim() || TRACKS[trackKey].title;
    const leadCaptured = params.quizLeadCaptured === 'true';

    let topTracks: string[] = [trackKey];
    try {
      const parsed = JSON.parse(params.quizTop ?? '[]');
      if (Array.isArray(parsed) && parsed.length > 0) {
        topTracks = parsed.filter((k): k is string => typeof k === 'string' && isValidTrackKey(k));
      }
    } catch {}

    let levels = { best: 'גבוהה מאוד', second: 'גבוהה', third: 'בינונית' };
    try {
      const parsed = JSON.parse(params.quizLevels ?? '{}');
      if (parsed && typeof parsed === 'object') {
        levels = {
          best: parsed.best ?? levels.best,
          second: parsed.second ?? levels.second,
          third: parsed.third ?? levels.third,
        };
      }
    } catch {}

    return { trackKey, trackTitle, topTracks, levels, leadCaptured };
  }, [params]);

  const top3Keys = useMemo(() => {
    const keys = ctx.topTracks.slice(0, 3);
    while (keys.length < 3) {
      // Pad with fallback tracks if fewer than 3 came through
      const fallbacks: TrackKey[] = ['digital_marketing', 'ui_ux', 'data_ai'];
      const next = fallbacks.find((k) => !keys.includes(k));
      if (next) keys.push(next); else break;
    }
    return keys as TrackKey[];
  }, [ctx.topTracks]);

  const medals = ['🥇', '🥈', '🥉'] as const;
  const rankLabels = ['התאמה הכי חזקה', 'התאמה מצוינת', 'אופציה מעולה'] as const;
  const rankLevels = [ctx.levels.best, ctx.levels.second, ctx.levels.third];

  const points = Number(params.quizPoints ?? 80);

  // ─── State ───────────────────────────────────────────────────────────────

  const [selectedTrack, setSelectedTrack] = useState<TrackKey>(ctx.trackKey);
  const [shareCopied, setShareCopied] = useState(false);

  // ─── Animation ───────────────────────────────────────────────────────────

  const revealAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (shouldReduceMotion(settings)) {
      revealAnim.setValue(1);
      return;
    }
    Animated.spring(revealAnim, { toValue: 1, tension: 50, friction: 8, useNativeDriver: true }).start();
  }, [revealAnim, settings]);

  const revealStyle = {
    opacity: revealAnim,
    transform: [{
      translateY: revealAnim.interpolate({ inputRange: [0, 1], outputRange: [30, 0] }),
    }],
  };

  // ─── Derived styles ───────────────────────────────────────────────────────

  const dynamic = useMemo(() => ({
    pageBg: { backgroundColor: getAccessibleBackgroundColor(theme.colors.background, settings) },
    text: { color: getAccessibleTextColor(theme.colors.text, settings) },
    textLight: { color: getAccessibleTextColor(theme.colors.textLight, settings) },
    surface: {
      backgroundColor: getAccessibleBackgroundColor(theme.colors.surface, settings),
      borderColor: getAccessibleColor(theme.colors.border, settings),
    },
    primaryBg: { backgroundColor: getAccessibleColor(theme.colors.primary, settings) },
    bold: { fontFamily: getAccessibleFontFamily(theme.fonts.bold, settings) },
    regular: { fontFamily: getAccessibleFontFamily(theme.fonts.regular, settings) },
    medium: { fontFamily: getAccessibleFontFamily(theme.fonts.medium, settings) },
  }), [settings]);

  // ─── Page engagement + page view ─────────────────────────────────────────
  usePageEngagement('/quiz/results');

  useEffect(() => {
    void trackPageView('/quiz/results');
    void trackButtonClick('quiz_results_page_load', '/quiz/results', {
      track: ctx.trackKey,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Navigation ───────────────────────────────────────────────────────────

  const goToRead = (key: TrackKey) => {
    void trackButtonClick('quiz_results_courses_cta', '/quiz/results', { track: key });
    router.push({
      pathname: '/catalog',
      params: { topic: TRACKS[key].title },
    });
  };
  const goHome = () => router.replace('/');

  const navigateToChat = useCallback(() => {
    void trackButtonClick('quiz_results_chat_cta', '/quiz/results', { track: selectedTrack });
    void trackFunnelStep('chat_start', { source: 'quiz_results', track: selectedTrack });
    const t = TRACKS[selectedTrack];
    router.replace({
      pathname: '/chat',
      params: {
        quizTrack: selectedTrack,
        quizTrackTitle: t.title,
        quizSource: 'quiz',
        quizLeadCaptured: 'true',
        quizTop: JSON.stringify(top3Keys),
        quizLevels: JSON.stringify(ctx.levels),
        skipWelcome: 'false',
      },
    });
  }, [router, selectedTrack, top3Keys, ctx.levels]);

  const handleShare = useCallback(async () => {
    void trackButtonClick('quiz_results_share', '/quiz/results', { track: selectedTrack });
    const copied = await shareResult(TRACKS[selectedTrack].title);
    if (copied) {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  }, [selectedTrack]);

  // ─── Guard: if no valid params, redirect back to quiz ─────────────────────

  useEffect(() => {
    if (!params.quizTrack || !isValidTrackKey(params.quizTrack)) {
      router.replace('/quiz');
    }
  }, [params.quizTrack, router]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <View style={[styles.page, dynamic.pageBg]}>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://kursiaii.com/quiz/results" />
        </Head>
      )}

      <Stack.Screen options={{ headerShown: false }} />

      {/* Colored tint from selected track */}
      <View
        style={[styles.bgTint, { backgroundColor: TRACKS[selectedTrack].color + '12', pointerEvents: 'none' }]}
      />

      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={goHome} style={styles.logoPress} accessibilityRole="button">
          <Text style={[styles.logoText, dynamic.bold, { color: theme.colors.primary }]}>Kursi</Text>
        </Pressable>
        <View style={styles.scoreBadge}>
          <Text style={[styles.scoreNum, dynamic.bold]}>⚡ {points}</Text>
        </View>
      </View>

      {/* Share toast */}
      {shareCopied && (
        <View style={styles.shareToast}>
          <Text style={[styles.shareToastText, dynamic.bold]}>🔗 הקישור הועתק!</Text>
        </View>
      )}

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          isWeb ? (styles.scrollContentWeb as any) : null,
        ]}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View style={[styles.inner, revealStyle]}>

          {/* Title */}
          <Text style={[styles.mainTitle, dynamic.bold, dynamic.text]}>
            התוצאות שלך 🎯
          </Text>
          <Text style={[styles.mainSub, dynamic.regular, dynamic.textLight]}>
            על סמך כל התשובות שלך, אלו התחומים שמתאימים לך
          </Text>

          {/* Top 3 track cards */}
          <View style={styles.trackGrid}>
            {top3Keys.map((key, idx) => {
              const t = TRACKS[key];
              const isSelected = selectedTrack === key;
              const level = rankLevels[idx] ?? 'גבוהה';

              return (
                <Pressable
                  key={key}
                  style={[
                    styles.trackCard,
                    dynamic.surface,
                    isSelected && { borderColor: t.color, borderWidth: 2.5 },
                  ]}
                  onPress={() => setSelectedTrack(key)}
                  accessibilityRole="button"
                  accessibilityLabel={`בחר ${t.title}`}
                  accessibilityState={{ selected: isSelected }}
                >
                  {/* Medal header */}
                  <View style={[styles.medalRow, { backgroundColor: t.color + '18' }]}>
                    <Text style={styles.medalEmoji}>{medals[idx]}</Text>
                    <Text style={[styles.rankLabel, dynamic.bold, { color: t.color }]}>
                      {rankLabels[idx]}
                    </Text>
                  </View>

                  {/* Title */}
                  <View style={styles.trackTitleRow}>
                    <Text style={styles.trackEmoji}>{t.emoji}</Text>
                    <Text style={[styles.trackTitle, dynamic.bold, dynamic.text]}>{t.title}</Text>
                  </View>

                  {/* Match badge */}
                  <View style={styles.matchRow}>
                    <View style={[styles.matchBadge, { backgroundColor: t.color + '25' }]}>
                      <Text style={[styles.matchText, dynamic.bold, { color: t.color }]}>
                        התאמה {level}
                      </Text>
                    </View>
                  </View>

                  {/* Subtitle */}
                  <Text style={[styles.trackSub, dynamic.regular, dynamic.textLight]} numberOfLines={2}>
                    {t.subtitle}
                  </Text>

                  {/* Bullets */}
                  <View style={styles.bullets}>
                    {t.bullets.map((b, i) => (
                      <View key={i} style={styles.bulletRow}>
                        <Text style={[styles.bulletCheck, { color: t.color }]}>✓</Text>
                        <Text style={[styles.bulletText, dynamic.regular, dynamic.text]}>{b}</Text>
                      </View>
                    ))}
                  </View>

                  {/* Read more */}
                  <TouchableOpacity
                    style={[styles.readMoreBtn, { borderColor: t.color, backgroundColor: t.color + '12' }]}
                    onPress={() => goToRead(key)}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.readMoreText, dynamic.bold, { color: t.color }]}>קרא עוד ←</Text>
                  </TouchableOpacity>

                  {/* Selected indicator */}
                  {isSelected && (
                    <View style={[styles.selectedIndicator, { backgroundColor: t.color }]}>
                      <Text style={styles.selectedIndicatorText}>נבחר</Text>
                    </View>
                  )}
                </Pressable>
              );
            })}
          </View>

          {/* ─── Dual CTA ───────────────────────────────────────────────── */}
          <View style={[styles.ctaBox, dynamic.surface]}>
            <Text style={[styles.ctaTitle, dynamic.bold, dynamic.text]}>
              בחרת: {TRACKS[selectedTrack].title} {TRACKS[selectedTrack].emoji}
            </Text>
            <Text style={[styles.ctaSub, dynamic.regular, dynamic.textLight]}>
              איך תרצה להמשיך?
            </Text>

            {/* Option A: Chat */}
            <Pressable
              style={({ pressed }) => [
                styles.ctaBtnPrimary,
                dynamic.primaryBg,
                pressed && !shouldReduceMotion(settings) ? styles.pressed : null,
              ]}
              onPress={navigateToChat}
              accessibilityRole="button"
              accessibilityLabel="דבר עם הבוט"
            >
              <Text style={[styles.ctaBtnText, dynamic.bold]}>
                💬 דבר עם הבוט — נמצא יחד את הקורס
              </Text>
              <Text style={[styles.ctaBtnSub, dynamic.regular]}>
                המלצה מותאמת אישית בשיחה
              </Text>
            </Pressable>

            {/* Option B: Filtered catalog */}
            <TouchableOpacity
              style={[styles.ctaBtnSecondary, { borderColor: TRACKS[selectedTrack].color }]}
              onPress={() => goToRead(selectedTrack)}
              activeOpacity={0.8}
            >
              <Text style={[styles.ctaBtnSecondaryText, dynamic.bold, { color: TRACKS[selectedTrack].color }]}>
                🔎 מצא לי קורסים בתחום הזה
              </Text>
              <Text style={[styles.ctaBtnSecondarySub, dynamic.regular, dynamic.textLight]}>
                קטלוג מסונן לפי {TRACKS[selectedTrack].title}
              </Text>
            </TouchableOpacity>

            {/* Share */}
            <TouchableOpacity
              style={styles.shareBtn}
              onPress={handleShare}
              activeOpacity={0.75}
              accessibilityRole="button"
              accessibilityLabel={shareCopied ? 'הקישור הועתק' : 'שתף את התוצאות'}
            >
              <Text style={[styles.shareBtnText, dynamic.medium, dynamic.textLight]}>
                {shareCopied ? '✅ הועתק!' : '🔗 שתף את התוצאות'}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Try again link */}
          <Pressable style={styles.retryBtn} onPress={() => router.replace('/quiz')} accessibilityRole="button">
            <Text style={[styles.retryText, dynamic.medium, { color: theme.colors.primary }]}>
              ← חזרה לשאלון
            </Text>
          </Pressable>

        </Animated.View>
      </ScrollView>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: { flex: 1 },
  bgTint: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    pointerEvents: 'none',
  } as any,

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: isWeb ? 28 : 16,
    paddingTop: Platform.OS === 'ios' ? 52 : 12,
    paddingBottom: 12,
  },
  logoPress: { paddingVertical: 4, paddingHorizontal: 4 },
  logoText: { fontSize: 20 },

  scoreBadge: {
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: 'rgba(200,83,30,0.18)',
  },
  scoreNum: { fontSize: 14, color: theme.colors.primary },

  shareToast: {
    marginHorizontal: isWeb ? 28 : 16,
    marginBottom: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(16,185,129,0.12)',
    borderRadius: 0,
    borderWidth: 1,
    borderColor: 'rgba(16,185,129,0.35)',
    alignItems: 'center',
  },
  shareToastText: { fontSize: 13, color: '#10b981' },

  scroll: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: isWeb ? 28 : 16,
    paddingTop: 8,
    paddingBottom: 40,
    width: '100%',
    alignSelf: 'center',
  },
  scrollContentWeb: { maxWidth: 720, width: '100%', alignSelf: 'center' } as any,

  inner: { width: '100%' },

  mainTitle: {
    fontSize: isWeb ? 30 : 24,
    textAlign: 'center',
    marginBottom: 8,
    lineHeight: 38,
  },
  mainSub: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 28,
    opacity: 0.8,
  },

  trackGrid: { gap: 16, marginBottom: 24 },

  trackCard: {
    borderWidth: 1.5,
    borderColor: theme.colors.border,
    borderRadius: 22,
    padding: isWeb ? 22 : 18,
    position: 'relative',
    overflow: 'hidden',
    ...(isWeb
      ? { boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.1, shadowRadius: 10, elevation: 4 }),
  } as any,

  medalRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 12,
    marginBottom: 14,
    gap: 6,
  },
  medalEmoji: { fontSize: 18 },
  rankLabel: { fontSize: 13 },

  trackTitleRow: { flexDirection: 'row-reverse', alignItems: 'center', marginBottom: 12, gap: 12 },
  trackEmoji: { fontSize: 28 },
  trackTitle: { fontSize: isWeb ? 20 : 18, lineHeight: 26 },

  matchRow: { flexDirection: 'row-reverse', alignItems: 'center', marginBottom: 12 },
  matchBadge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  matchText: { fontSize: 13 },

  trackSub: { fontSize: 13, textAlign: 'right', lineHeight: 20, marginBottom: 14, opacity: 0.8 },

  bullets: { gap: 8, marginBottom: 16 },
  bulletRow: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8 },
  bulletCheck: { fontSize: 15, fontWeight: '700', flexShrink: 0 },
  bulletText: { flex: 1, fontSize: 13, textAlign: 'right', lineHeight: 20 },

  readMoreBtn: {
    borderWidth: 1.5,
    borderRadius: 0,
    paddingVertical: 11,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  readMoreText: { fontSize: 14, fontWeight: '700' },

  selectedIndicator: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderBottomLeftRadius: 12,
  },
  selectedIndicatorText: { color: '#fff', fontSize: 11, fontWeight: '700' },

  pressed: { opacity: 0.86, transform: [{ scale: 0.985 }] },

  ctaBox: {
    borderWidth: 1,
    borderRadius: 0,
    padding: isWeb ? 28 : 20,
    alignItems: 'center',
    marginBottom: 20,
    ...(isWeb
      ? { boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.15, shadowRadius: 16, elevation: 8 }),
  } as any,

  ctaTitle: { fontSize: isWeb ? 22 : 19, textAlign: 'center', marginBottom: 6 },
  ctaSub: { fontSize: 14, textAlign: 'center', marginBottom: 20, opacity: 0.75 },

  ctaBtnPrimary: {
    width: '100%',
    borderRadius: 0,
    paddingVertical: 18,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginBottom: 12,
    ...(isWeb
      ? { boxShadow: `0 6px 20px ${theme.colors.primary}50` }
      : { shadowColor: theme.colors.primary, shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.35, shadowRadius: 12, elevation: 6 }),
  } as any,
  ctaBtnText: { color: '#fff', fontSize: 16, textAlign: 'center', lineHeight: 22 },
  ctaBtnSub: { color: 'rgba(255,255,255,0.75)', fontSize: 12, textAlign: 'center', marginTop: 4 },

  ctaBtnSecondary: {
    width: '100%',
    borderRadius: 0,
    borderWidth: 2,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginBottom: 16,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  ctaBtnSecondaryText: { fontSize: 15, textAlign: 'center', lineHeight: 22 },
  ctaBtnSecondarySub: { fontSize: 12, textAlign: 'center', marginTop: 4, opacity: 0.7 },

  shareBtn: { paddingVertical: 10, paddingHorizontal: 20 },
  shareBtnText: { fontSize: 14, textAlign: 'center' },

  retryBtn: { alignSelf: 'center', paddingVertical: 12, paddingHorizontal: 16 },
  retryText: { fontSize: 14, textDecorationLine: 'underline' },
});
